import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Loader2, Mail, TicketX, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/branding/brand-logo';
import { AccountMenu } from '@/components/common/account-menu';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAcceptOrganizationInvitation } from '@/features/organizations/hooks/useAcceptOrganizationInvitation';
import { useDeclineOrganizationInvitation } from '@/features/organizations/hooks/useDeclineOrganizationInvitation';
import { useCurrentOrganizationStore } from '@/features/organizations/store/useCurrentOrganizationStore';
import {
    resolveOrganizationInvitation,
    type ResolvedOrganizationInvitation
} from '@/features/organizations/services/invitations';
import {
    apiErrorStatusFallbackPresets,
    resolveApiErrorMessage,
    type ApiErrorStatusFallbacks
} from '@/lib/api-error-translations';
import { useAuth } from '@/providers/useAuth';

const INVITATION_AUTH_REDIRECT_DELAY_MS = 3000;

export const Route = createFileRoute('/invitations/accept')({
    validateSearch: (search: Record<string, unknown>) => ({
        token: typeof search.token === 'string' ? search.token.trim() : ''
    }),
    component: InvitationAcceptPage
});

function InvitationAcceptPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { token } = Route.useSearch();
    const { isReady, isAuthenticated, email, login, register } = useAuth();
    const acceptInvitation = useAcceptOrganizationInvitation();
    const declineInvitation = useDeclineOrganizationInvitation();
    const setCurrentOrganizationId = useCurrentOrganizationStore((state) => state.setCurrentOrganizationId);

    const [successfulAction, setSuccessfulAction] = useState<'accepted' | 'declined' | null>(null);
    const [resolvedInvitationOverride, setResolvedInvitationOverride] = useState<ResolvedOrganizationInvitation | null>(
        null
    );
    const [actionError, setActionError] = useState<string | null>(null);
    const authRedirectRequestedRef = useRef(false);

    const invitationQuery = useQuery({
        queryKey: ['organization-invitation', 'resolve', token],
        queryFn: () => resolveOrganizationInvitation(token),
        enabled: token.length > 0,
        retry: false
    });

    const displayedInvitation = resolvedInvitationOverride ?? invitationQuery.data;
    const invitedEmail = displayedInvitation?.email?.trim().toLowerCase() ?? null;
    const currentEmail = email?.trim().toLowerCase() ?? null;
    const accountMismatch = Boolean(invitedEmail && currentEmail && invitedEmail !== currentEmail);
    const shouldRegisterInvitee = displayedInvitation?.invitedUserExists === false;

    const handleAccept = () => {
        if (!token || !invitationQuery.data) {
            return;
        }

        setActionError(null);

        acceptInvitation
            .mutateAsync(token)
            .then(() => {
                setSuccessfulAction('accepted');

                if (invitationQuery.data.organizationId) {
                    setCurrentOrganizationId(invitationQuery.data.organizationId);
                }
            })
            .catch((error) => {
                setActionError(resolveAcceptanceError(error, t));
            });
    };

    const handleDecline = () => {
        if (!token) {
            return;
        }

        setActionError(null);

        declineInvitation
            .mutateAsync(token)
            .then((invitation) => {
                setResolvedInvitationOverride(invitation);
                setSuccessfulAction('declined');
            })
            .catch((error) => {
                setActionError(resolveDeclineError(error, t));
            });
    };

    useEffect(() => {
        if (!successfulAction) {
            return;
        }

        const redirectTimer = window.setTimeout(() => {
            void navigate({ to: '/organizations' });
        }, 1200);

        return () => window.clearTimeout(redirectTimer);
    }, [navigate, successfulAction]);

    const blockingError = !token
        ? t('organizationInvitationAcceptance.errors.missingToken')
        : invitationQuery.isError
          ? resolveInvitationError(invitationQuery.error, t)
          : null;
    const resolvedError = blockingError ?? actionError;
    const invitationState = getInvitationState(displayedInvitation);
    const isInvitationPending = invitationState === 'pending';

    useEffect(() => {
        if (
            !isReady ||
            isAuthenticated ||
            !displayedInvitation ||
            !isInvitationPending ||
            authRedirectRequestedRef.current ||
            blockingError ||
            successfulAction
        ) {
            return;
        }

        const authRedirectTimer = window.setTimeout(() => {
            if (authRedirectRequestedRef.current) {
                return;
            }

            authRedirectRequestedRef.current = true;

            const authOptions = invitedEmail ? { loginHint: invitedEmail } : undefined;

            if (shouldRegisterInvitee) {
                void register(authOptions);

                return;
            }

            void login(authOptions);
        }, INVITATION_AUTH_REDIRECT_DELAY_MS);

        return () => window.clearTimeout(authRedirectTimer);
    }, [
        blockingError,
        displayedInvitation,
        invitedEmail,
        isAuthenticated,
        isInvitationPending,
        isReady,
        login,
        register,
        shouldRegisterInvitee,
        successfulAction
    ]);

    const successMessage = successfulAction
        ? t(
              successfulAction === 'accepted'
                  ? 'organizationInvitationAcceptance.success'
                  : 'organizationInvitationAcceptance.declinedSuccess'
          )
        : null;
    const successDescription = successfulAction
        ? t(
              successfulAction === 'accepted'
                  ? 'organizationInvitationAcceptance.redirecting'
                  : 'organizationInvitationAcceptance.declinedSuccessDescription'
          )
        : null;
    const isAccepting = isAuthenticated && acceptInvitation.isPending;
    const isDeclining = isAuthenticated && declineInvitation.isPending;
    const isProcessing = isAccepting || isDeclining;
    const canActOnInvitation = Boolean(
        isReady &&
        isAuthenticated &&
        invitationQuery.data &&
        !accountMismatch &&
        !blockingError &&
        !successfulAction &&
        isInvitationPending
    );
    const shouldRedirectToAuth =
        isReady &&
        !isAuthenticated &&
        !blockingError &&
        !successfulAction &&
        Boolean(displayedInvitation) &&
        isInvitationPending;
    const loginPromptAction = shouldRegisterInvitee
        ? t('organizationInvitationAcceptance.registerAction')
        : t('organizationInvitationAcceptance.loginAction');

    return (
        <div className='min-h-svh bg-muted/20'>
            <InvitationPageHeader />
            <main className='flex min-h-[calc(100svh-56px)] items-center justify-center px-4 py-12'>
                <Card className='w-full max-w-xl'>
                    <CardHeader>
                        <CardTitle>{t('organizationInvitationAcceptance.pageTitle')}</CardTitle>
                        <CardDescription>{t('organizationInvitationAcceptance.pageDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className='space-y-4'>
                            {!isReady ? (
                            <p className='text-sm text-muted-foreground'>
                                {t('organizationInvitationAcceptance.preparingAuth')}
                            </p>
                        ) : null}

                        {!token ? (
                            <StateMessage
                                icon={TicketX}
                                title={t('organizationInvitationAcceptance.errorTitle')}
                                description={t('organizationInvitationAcceptance.errors.missingToken')}
                                tone='error'
                            />
                        ) : null}

                        {token && invitationQuery.isPending ? (
                            <div className='space-y-2'>
                                <p className='text-sm font-medium'>{t('organizationInvitationAcceptance.resolving')}</p>
                                <p className='text-sm text-muted-foreground'>
                                    {t('organizationInvitationAcceptance.resolvingDescription')}
                                </p>
                            </div>
                        ) : null}

                        {shouldRedirectToAuth ? (
                            <AuthRedirectLoadingState
                                title={loginPromptAction}
                                description={t('organizationInvitationAcceptance.preparingAuth')}
                            />
                        ) : null}

                        {displayedInvitation && !shouldRedirectToAuth ? (
                            <InvitationDetailsCard invitation={displayedInvitation} invitationState={invitationState} />
                        ) : null}

                        {accountMismatch ? (
                            <StateMessage
                                icon={AlertTriangle}
                                title={t('organizationInvitationAcceptance.wrongAccountTitle')}
                                description={t('organizationInvitationAcceptance.wrongAccountDescription', {
                                    invitedEmail: displayedInvitation?.email ?? '',
                                    currentEmail: email ?? ''
                                })}
                                tone='warning'
                            />
                        ) : null}

                        {canActOnInvitation ? (
                            <div className='grid gap-2 sm:grid-cols-2'>
                                <Button onClick={handleAccept} disabled={isProcessing} className='w-full'>
                                    <CheckCircle2 className='h-4 w-4' />
                                    {t('organizationInvitationAcceptance.acceptAction')}
                                </Button>
                                <Button
                                    type='button'
                                    variant='outline'
                                    onClick={handleDecline}
                                    disabled={isProcessing}
                                    className='w-full'
                                >
                                    <TicketX className='h-4 w-4' />
                                    {t('organizationInvitationAcceptance.declineAction')}
                                </Button>
                            </div>
                        ) : null}

                        {isProcessing ? (
                            <div className='space-y-2'>
                                <p className='text-sm font-medium'>
                                    {t(
                                        isAccepting
                                            ? 'organizationInvitationAcceptance.accepting'
                                            : 'organizationInvitationAcceptance.declining'
                                    )}
                                </p>
                                <p className='text-sm text-muted-foreground'>
                                    {t(
                                        isAccepting
                                            ? 'organizationInvitationAcceptance.acceptingDescription'
                                            : 'organizationInvitationAcceptance.decliningDescription'
                                    )}
                                </p>
                            </div>
                        ) : null}

                        {resolvedError ? (
                            <StateMessage
                                icon={TicketX}
                                title={t('organizationInvitationAcceptance.errorTitle')}
                                description={resolvedError}
                                tone='error'
                            />
                        ) : null}

                        {successMessage ? (
                            <StateMessage
                                icon={CheckCircle2}
                                title={successMessage}
                                description={successDescription ?? ''}
                                tone='success'
                            />
                            ) : null}
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}

function AuthRedirectLoadingState({ title, description }: { title: string; description: string }) {
    return (
        <div className='flex flex-col items-center gap-4 rounded-lg border border-border bg-muted/20 p-6 text-center text-foreground'>
            <div className='rounded-full bg-background p-3 shadow-none ring-1 ring-border'>
                <Loader2 className='h-6 w-6 animate-spin text-primary' aria-hidden='true' />
            </div>
            <div className='space-y-1'>
                <p className='text-sm font-medium text-foreground'>{title}</p>
                <p className='text-sm text-muted-foreground'>{description}</p>
            </div>
        </div>
    );
}

function InvitationPageHeader() {
    return (
        <header className='sticky top-0 z-40 border-b bg-background/95 text-foreground backdrop-blur'>
            <div className='mx-auto flex h-14 max-w-[1440px] items-center gap-4 px-5'>
                <div className='flex items-center'>
                    <BrandLogo />
                </div>
                <div className='ml-auto flex items-center gap-3'>
                    <AccountMenu />
                </div>
            </div>
        </header>
    );
}

function InvitationDetailsCard({
    invitation,
    invitationState
}: {
    invitation: ResolvedOrganizationInvitation;
    invitationState: string;
}) {
    const { t } = useTranslation();

    return (
        <div className='space-y-3 rounded-lg border bg-background p-4'>
            <div className='flex items-start gap-3'>
                <Users className='mt-0.5 h-4 w-4 text-muted-foreground' />
                <div className='space-y-1'>
                    <p className='text-sm font-medium'>
                        {t('organizationInvitationAcceptance.invitationDetailsTitle')}
                    </p>
                    <p className='text-sm text-muted-foreground'>
                        {t('organizationInvitationAcceptance.invitationDetailsDescription')}
                    </p>
                </div>
            </div>
            <dl className='grid gap-3 text-sm sm:grid-cols-2'>
                <DetailItem
                    label={t('organizationInvitationAcceptance.fields.organization')}
                    value={invitation.organizationName ?? t('organizationInvitationAcceptance.unknownOrganization')}
                />
                <DetailItem
                    label={t('organizationInvitationAcceptance.fields.role')}
                    value={t(`organizationInvitations.roles.${invitation.role}`)}
                />
                <DetailItem
                    label={t('organizationInvitationAcceptance.fields.email')}
                    value={invitation.email}
                    icon={Mail}
                    className='sm:col-span-2'
                />
                <DetailItem
                    label={t('organizationInvitationAcceptance.fields.status')}
                    value={t(`organizationInvitations.statuses.${invitationState}`)}
                />
            </dl>
        </div>
    );
}

function DetailItem({
    label,
    value,
    icon: Icon,
    className
}: {
    label: string;
    value: string;
    icon?: typeof Mail;
    className?: string;
}) {
    return (
        <div className={`min-w-0 space-y-1 rounded-md border bg-muted/20 p-3 ${className ?? ''}`}>
            <p className='text-xs uppercase tracking-wide text-muted-foreground'>{label}</p>
            <div className='flex min-w-0 items-center gap-2'>
                {Icon ? <Icon className='h-4 w-4 shrink-0 text-muted-foreground' /> : null}
                <p className='min-w-0 overflow-hidden break-all font-medium text-foreground'>{value}</p>
            </div>
        </div>
    );
}

function StateMessage({
    icon: Icon,
    title,
    description,
    tone = 'default'
}: {
    icon: typeof CheckCircle2;
    title: string;
    description: string;
    tone?: 'default' | 'warning' | 'error' | 'success';
}) {
    const toneClassName = {
        default: 'border border-border bg-muted/30 text-muted-foreground',
        warning: 'border border-border bg-muted/20 text-foreground',
        error: 'border border-destructive/30 bg-destructive/5 text-destructive',
        success: 'border border-primary/20 bg-primary/5 text-foreground'
    }[tone];

    const iconClassName = tone === 'success' ? 'text-primary' : tone === 'warning' ? 'text-primary' : '';

    return (
        <div className={`flex items-start gap-3 rounded-lg p-4 text-sm ${toneClassName}`}>
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconClassName}`} />
            <div>
                <p className='font-medium'>{title}</p>
                <p>{description}</p>
            </div>
        </div>
    );
}

function getInvitationState(invitation?: ResolvedOrganizationInvitation): string {
    if (!invitation) {
        return 'pending';
    }

    return invitation.status;
}

function resolveInvitationActionError(
    error: unknown,
    t: (key: string, options?: Record<string, unknown>) => string,
    statusFallbacks: ApiErrorStatusFallbacks
): string {
    return resolveApiErrorMessage(error, t, {
        fallbackKey: 'apiErrors.organizationInvitations.actionFailed',
        statusFallbacks
    });
}

function resolveInvitationError(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
    return resolveInvitationActionError(error, t, apiErrorStatusFallbackPresets.organizationInvitationResolve);
}

function resolveAcceptanceError(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
    return resolveInvitationActionError(error, t, apiErrorStatusFallbackPresets.organizationInvitationAccept);
}

function resolveDeclineError(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
    return resolveInvitationActionError(error, t, apiErrorStatusFallbackPresets.organizationInvitationDecline);
}
